import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api } from '../../lib/api';
import shellStyles from '../../components/shell/shell.module.css';
import ui from '../../styles/ui.module.css';

/** P8 — configurar a entrega do canal escolhido em P7. Um painel por canal (ADR 0019 §5 L2).
 *  `digital_file`/`digital_link` não têm leitura de estado no backend ainda (só escrita) —
 *  mostramos confirmação da própria sessão, não um "já configurado" vindo do servidor. */
export function ProdutoEntrega() {
  const { id } = useParams<{ id: string }>();
  const products = useQuery({ queryKey: ['products'], queryFn: () => api.products() });
  const product = products.data?.find((p) => p.id === id);

  return (
    <>
      <div className={shellStyles.head}>
        <Link className={ui.faint} to="/produtos">
          ← Produtos
        </Link>
        <h1>{product?.name ?? 'Entrega'}</h1>
        <p>Como o cliente recebe depois de pagar.</p>
      </div>

      {!id ? null : products.isLoading ? (
        <p className={ui.empty}>Carregando…</p>
      ) : !product ? (
        <p className={ui.empty}>Produto não encontrado.</p>
      ) : (
        <div className={ui.card} style={{ padding: '18px 20px' }}>
          {product.deliveryChannel === 'digital_file' ? <FileConfig productId={id} /> : null}
          {product.deliveryChannel === 'digital_link' ? <LinkConfig productId={id} /> : null}
          {product.deliveryChannel === 'video' ? <VideoConfig productId={id} /> : null}
          {product.deliveryChannel === 'telegram' ? <TelegramConfig productId={id} /> : null}
          {product.deliveryChannel === 'physical' ? <PhysicalConfig /> : null}
        </div>
      )}
    </>
  );
}

function FileConfig({ productId }: { productId: string }) {
  const [sentName, setSentName] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const upload = useMutation({
    mutationFn: (file: File) => api.uploadDeliveryFile(productId, file),
    onSuccess: (_r, file) => {
      setSentName(file.name);
      setError(null);
    },
    onError: (e: unknown) => setError(e instanceof Error ? e.message : 'Falha no envio'),
  });

  return (
    <div className={ui.form}>
      <p className={ui.muted}>O cliente baixa este arquivo por um link assinado depois de pagar.</p>
      <label className={ui.field}>
        Arquivo
        <input
          type="file"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) upload.mutate(file);
          }}
        />
      </label>
      {upload.isPending ? <p className={ui.muted}>Enviando…</p> : null}
      {sentName ? <p className={ui.success}>✓ {sentName} enviado — já está valendo pros próximos pedidos.</p> : null}
      {error ? <p className={ui.error}>{error}</p> : null}
    </div>
  );
}

function LinkConfig({ productId }: { productId: string }) {
  const [value, setValue] = useState('');
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: () => api.setDeliveryLink(productId, value),
    onSuccess: () => {
      setSaved(true);
      setError(null);
    },
    onError: (e: unknown) => setError(e instanceof Error ? e.message : 'Falha ao salvar'),
  });

  return (
    <form
      className={ui.form}
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate();
      }}
    >
      <label className={ui.field}>
        Link ou instrução que o cliente recebe
        <textarea rows={3} required value={value} onChange={(e) => setValue(e.target.value)} />
      </label>
      {error ? <p className={ui.error}>{error}</p> : null}
      {saved ? <p className={ui.success}>✓ Salvo — já está valendo pros próximos pedidos.</p> : null}
      <div className={ui.actions}>
        <button className={`${ui.btn} ${ui.btnPrimary}`} type="submit" disabled={save.isPending}>
          {save.isPending ? 'Salvando…' : 'Salvar'}
        </button>
      </div>
    </form>
  );
}

function VideoConfig({ productId }: { productId: string }) {
  const qc = useQueryClient();
  const [title, setTitle] = useState('');
  const [error, setError] = useState<string | null>(null);
  const videos = useQuery({ queryKey: ['videos', productId], queryFn: () => api.videos(productId) });
  const upload = useMutation({
    mutationFn: (file: File) => api.uploadVideo(productId, file, title || undefined),
    onSuccess: () => {
      setTitle('');
      setError(null);
      qc.invalidateQueries({ queryKey: ['videos', productId] });
    },
    onError: (e: unknown) => setError(e instanceof Error ? e.message : 'Falha no envio'),
  });

  return (
    <div className={ui.form}>
      <p className={ui.muted}>O cliente assiste na área do cliente da loja (/conta) — sem download.</p>
      <label className={ui.field}>
        Título do vídeo
        <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Aula 1 — Introdução" />
      </label>
      <label className={ui.field}>
        Arquivo de vídeo
        <input
          type="file"
          accept="video/*"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) upload.mutate(file);
          }}
        />
      </label>
      {upload.isPending ? <p className={ui.muted}>Enviando…</p> : null}
      {error ? <p className={ui.error}>{error}</p> : null}

      <div className={ui.secH} style={{ marginTop: '0.5rem' }}>
        <h2>Vídeos enviados</h2>
      </div>
      {!videos.data?.length ? (
        <p className={ui.faint}>Nenhum vídeo ainda.</p>
      ) : (
        <ol style={{ paddingLeft: '1.1rem', display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
          {videos.data.map((v) => (
            <li key={v.id} className={ui.muted}>
              {v.title} <span className={ui.faint}>· {(v.sizeBytes / 1024 / 1024).toFixed(1)} MB</span>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

function TelegramConfig({ productId }: { productId: string }) {
  const qc = useQueryClient();
  const [chatId, setChatId] = useState('');
  const [title, setTitle] = useState('');
  const [error, setError] = useState<string | null>(null);
  const current = useQuery({ queryKey: ['telegramGroup', productId], queryFn: () => api.telegramGroup(productId) });
  const link = useMutation({
    mutationFn: () => api.linkTelegramGroup(productId, chatId, title),
    onSuccess: () => {
      setError(null);
      qc.invalidateQueries({ queryKey: ['telegramGroup', productId] });
    },
    onError: (e: unknown) => setError(e instanceof Error ? e.message : 'Falha ao vincular'),
  });

  return (
    <div className={ui.form}>
      <p className={ui.muted}>
        O cliente entra no grupo automaticamente depois de pagar (pedido de entrada aprovado). Pegue o{' '}
        <b>chat ID</b> do grupo — encaminhe uma mensagem dele pro{' '}
        <span className={ui.mono}>@userinfobot</span> no Telegram.
      </p>

      {current.data ? (
        <div className={ui.card} style={{ padding: '12px 14px' }}>
          <p className={ui.muted}>
            Vinculado a <b>{current.data.title}</b>
          </p>
          <span className={`${ui.pill} ${current.data.botIsAdmin ? ui.pillPos : ui.pillWarn}`}>
            {current.data.botIsAdmin ? 'Bot é admin' : 'Bot ainda não é admin do grupo'}
          </span>
        </div>
      ) : null}

      <form
        onSubmit={(e) => {
          e.preventDefault();
          link.mutate();
        }}
        className={ui.form}
      >
        <label className={ui.field}>
          Chat ID do grupo
          <input required value={chatId} onChange={(e) => setChatId(e.target.value)} placeholder="-1001234567890" />
        </label>
        <label className={ui.field}>
          Nome do grupo
          <input required value={title} onChange={(e) => setTitle(e.target.value)} />
        </label>
        {error ? <p className={ui.error}>{error}</p> : null}
        <div className={ui.actions}>
          <button className={`${ui.btn} ${ui.btnPrimary}`} type="submit" disabled={link.isPending}>
            {link.isPending ? 'Vinculando…' : 'Vincular grupo'}
          </button>
        </div>
      </form>
    </div>
  );
}

function PhysicalConfig() {
  return (
    <p className={ui.muted}>
      Nada a configurar aqui — você recebe o endereço do cliente no pedido e marca como enviado em{' '}
      <Link to="/vendas">Vendas</Link>.
    </p>
  );
}
